import { IHrefNavItem, IMenuNavItem, ISubmenuNavItem } from '@web_opxp/components';
import { LanguageService } from '@web_opxp/features';
import * as HistoryService from '../../base/historyService';

const onboardingItem: IHrefNavItem = {
  kind: 'href',
  label: 'common.common:Header.Menu.Start',
  // link is empty while it will be rewritten in menu generation function
  link: '',
};

const blogItem: IHrefNavItem = {
  kind: 'href',
  label: 'common.common:Header.Menu.Blog',
  // link is empty while it will be rewritten in menu generation function
  link: '',
};

const getBlogItem = (language: string): IHrefNavItem => {
  return {
    ...blogItem,
    label: LanguageService.getTFunction()(blogItem.label),
    link: HistoryService.getBlogUrl(language),
  };
};

const startTrainingItem: IHrefNavItem = {
  kind: 'href',
  label: 'common.common:Header.Menu.Training',
  // link is relative path to feature on corresponding domain
  link: '',
};

const extrasItem: IHrefNavItem = {
  kind: 'href',
  label: 'common:Header.Menu.Extras',
  link: '/extras',
};

const MenuAchievements: IHrefNavItem = {
  kind: 'href',
  link: '/achievements',
  label: 'common:Header.Menu.Achievements',
};

const MenuStatistics: IHrefNavItem = {
  kind: 'href',
  link: '/evaluation/statistics',
  label: 'common:Header.Menu.Statistics',
};

const MenuCfq: IHrefNavItem = {
  kind: 'href',
  link: '/evaluation/cfq',
  label: 'common:Header.Menu.SelfAssessment',
};

const MenuProgress: IHrefNavItem = {
  kind: 'href',
  link: '/evaluation/performance',
  label: 'common:Header.Menu.Evaluation',
};

const MenuTrainingOverview: IHrefNavItem = {
  kind: 'href',
  link: '/evaluation',
  label: 'common:Header.Menu.TrainingOverview',
};

const premiumItem: IHrefNavItem = {
  kind: 'href',
  label: 'common:Header.Menu.Premium',
  // link will be replaced with proper url in runtime
  link: '/',
};

const getUserItem = (
  label: string,
  items: IMenuNavItem[],
  badge?: string | number,
  avatar?: string,
  onClick?: React.MouseEventHandler<HTMLButtonElement>
): ISubmenuNavItem => ({
  kind: 'menu',
  label,
  items,
  badge,
  avatar,
  onClick,
});

function getProgressSubmenu(hrefGenerator: (link: string) => string): ISubmenuNavItem {
  const items: IHrefNavItem[] = [MenuTrainingOverview, MenuProgress, MenuCfq, MenuStatistics, MenuAchievements];
  return {
    kind: 'menu',
    label: LanguageService.getTFunction()('common:Header.Menu.Progress'),
    link: hrefGenerator('/evaluation'),
    items: items.map((i) => ({ ...i, label: LanguageService.getTFunction()(i.label), link: hrefGenerator(i.link) })),
  };
}

export const getAuthorizedMenu = (language?: string, session?: string) => {
  const hrefGenerator = (link: string) => HistoryService.getTrainingUrl(link, language, session);

  const start: IHrefNavItem = {
    ...startTrainingItem,
    label: LanguageService.getTFunction()(startTrainingItem.label),
    link: hrefGenerator(startTrainingItem.link),
  };

  const progress = getProgressSubmenu(hrefGenerator);

  const extras: IHrefNavItem = {
    ...extrasItem,
    label: LanguageService.getTFunction()(extrasItem.label),
    link: hrefGenerator(extrasItem.link),
  };
  return [start, progress, extras];
};

export const getPremiumMenu = (language: string, session?: string) => {
  const baseMenu = getAuthorizedMenu(language, session);
  const blog = getBlogItem(language);
  return [...baseMenu, blog];
};

export const getFreemiumMenu = (language: string, session?: string) => {
  const baseMenu = getAuthorizedMenu(language, session);
  const premium = {
    ...premiumItem,
    label: LanguageService.getTFunction()(premiumItem.label),
    link: HistoryService.getInternalUrl(premiumItem.link),
  };
  const blog = getBlogItem(language);
  return [...baseMenu, premium, blog];
};

const getAnonymousMenu = (language: string): IMenuNavItem[] => {
  const onboarding = {
    ...onboardingItem,
    label: LanguageService.getTFunction()(onboardingItem.label),
    link: HistoryService.getOnboardingUrl(language),
  };
  const blog = getBlogItem(language);
  return [onboarding, blog];
};

export const getMenu = (
  authorized: boolean,
  premium: boolean,
  language: string,
  session?: string,
  userLabel = '',
  userItems: IMenuNavItem[] = [],
  userBadge?: string | number,
  userAvatar?: string,
  userClick?: React.MouseEventHandler<HTMLButtonElement>
) => {
  let menu: IMenuNavItem[];

  if (authorized) {
    if (premium) {
      menu = getPremiumMenu(language, session);
    } else {
      menu = getFreemiumMenu(language, session);
    }
  } else {
    menu = getAnonymousMenu(language);
  }
  return [...menu, getUserItem(userLabel, userItems, userBadge, userAvatar, userClick)];
};

export default getMenu;
